/**
 * lib/cameraController.js — canonical camera command authority (IP-002).
 *
 * SINGLE OWNER of viewport mutation. Every camera change is a NAMED
 * command dispatched to this reducer. Direct mutation of viewport
 * state (setVp / setViewport) outside of `dispatch()` is forbidden;
 * dev builds enforce this via the mutation ledger below.
 *
 * ─── Command vocabulary ──────────────────────────────────────────────
 *
 *   INITIAL_FIT                    — fresh sheet, fit + centre
 *   RESTORE_VIEWPORT               — replay a persisted viewport
 *   USER_PAN                       — pointer drag / touch drag
 *   USER_WHEEL_ZOOM                — wheel event (mouse/trackpad)
 *   USER_TRACKPAD_ZOOM             — pinch-on-trackpad (ctrl+wheel)
 *   USER_PINCH_ZOOM                — two-finger touch pinch
 *   USER_BUTTON_ZOOM               — +/- controls
 *   FOCUS_OBJECT                   — jump-to for pins/objects
 *   PRESERVE_CENTER_AFTER_RESIZE   — container resized; keep normalized centre
 *   RESET_VIEW                     — explicit user reset
 *   ROTATE_VIEW                    — reserved (rotation not yet enabled)
 *   CANCEL_INERTIA                 — new gesture cancels prior inertia
 *
 * Every command carries provenance (source, user_initiated) so the
 * ledger can attribute every mutation.
 *
 * ─── Invariants ──────────────────────────────────────────────────────
 *
 *   - Tile-level promotion does NOT dispatch a camera command
 *     (scale changes only via the six USER_* commands).
 *   - Panel toggle dispatches PRESERVE_CENTER_AFTER_RESIZE, NOT a refit.
 *   - Metadata / image / DPR callbacks NEVER dispatch anything except
 *     RESTORE_VIEWPORT (only during the INITIAL_FIT lifecycle).
 *   - USER_WHEEL_ZOOM / USER_TRACKPAD_ZOOM / USER_PINCH_ZOOM MUST
 *     supply an anchor point; the world point beneath the anchor
 *     stays put across the mutation.
 */

import {
  fitAndCenter,
  zoomAroundPoint,
  computeFitScale,
  clampScale,
  isPlausibleViewport,
  centerOnNormalizedPoint,
} from "./camera";

// ─── Dev-mode mutation ledger ────────────────────────────────────────
//
// In development builds every camera state transition is recorded so
// tests (and the developer console) can assert:
//   `unauthorized_camera_mutations = 0`
//
// A transition is UNAUTHORIZED if:
//   - `dispatch({ type })` uses a type outside COMMAND_TYPES,  OR
//   - viewport state changes without a dispatch.
//
// Production builds skip the ledger entirely.

const COMMAND_TYPES = new Set([
  "INITIAL_FIT",
  "RESTORE_VIEWPORT",
  "USER_PAN",
  "USER_WHEEL_ZOOM",
  "USER_TRACKPAD_ZOOM",
  "USER_PINCH_ZOOM",
  "USER_BUTTON_ZOOM",
  "FOCUS_OBJECT",
  "PRESERVE_CENTER_AFTER_RESIZE",
  "RESET_VIEW",
  "ROTATE_VIEW",
  "CANCEL_INERTIA",
  // System-driven per-frame commit used by inertia decay and snap-back
  // animations. Distinct from USER_* so ledger provenance can tell
  // "operator dragged" from "system rebounded to natural bounds".
  "APPLY_ANIMATION_FRAME",
]);

const IS_DEV = (
  typeof process !== "undefined" &&
  process.env &&
  process.env.NODE_ENV !== "production"
);

// Ledger is populated ALWAYS. Records are ~200 bytes each and capped
// implicitly by session length. The cost is negligible and the safety
// benefit — cross-browser automated proof of zero unauthorized camera
// mutations — is the completion gate for IP-001+IP-002 Step 8. The
// `IS_DEV` constant is preserved for future gating decisions.
void IS_DEV;

const _ledger = [];

/** Return a copy of the mutation ledger (dev only; empty in prod). */
export function getMutationLedger() {
  return _ledger.slice();
}

/** Clear the ledger. Used at the start of each unit test. */
export function clearMutationLedger() {
  _ledger.length = 0;
}

/** Count of unauthorized entries in the ledger. Always 0 unless a
 *  developer routed a mutation around the reducer. */
export function countUnauthorizedMutations() {
  return _ledger.filter((e) => e.unauthorized).length;
}

function record(entry) {
  _ledger.push({ ts: Date.now(), ...entry });
  _ensureWindowExposure();
}

// Expose ledger read-only for cross-browser automated probes.
// Attached lazily on the first record() call so the module works
// regardless of webpack's NODE_ENV inlining and regardless of whether
// the browser runs webpack dev or a preview production build.
let _windowExposed = false;
function _ensureWindowExposure() {
  if (_windowExposed) return;
  if (typeof window === "undefined") return;
  try {
    if (!window.__cameraLedger) {
      window.__cameraLedger = () => ({
        size: _ledger.length,
        unauthorized: _ledger.filter((e) => e.unauthorized).length,
        lastCommand: _ledger.length ? _ledger[_ledger.length - 1].type : null,
        sampleUnauthorized: _ledger
          .filter((e) => e.unauthorized)
          .slice(0, 5),
      });
    }
    _windowExposed = true;
  } catch (_e) {
    // ignore — non-browser env
  }
}

// ─── Reducer helpers ─────────────────────────────────────────────────

const DEFAULT_VP = { tx: 0, ty: 0, s: 1 };

function _clamp(next, meta) {
  if (!meta || !meta.imgDims || !meta.containerDims) return next;
  const fit = computeFitScale(meta.imgDims, meta.containerDims) || 1;
  const maxZoom = meta.maxZoom ?? 8;
  const s = clampScale(next.s, fit, maxZoom);
  if (s === next.s) return next;
  // Scale was clamped — recompute translation so the anchor stays put
  // if an anchor was provided; otherwise scale around container centre.
  const anchor = meta.anchor || {
    x: (meta.containerDims.w || 0) / 2,
    y: (meta.containerDims.h || 0) / 2,
  };
  const imageX = (anchor.x - next.tx) / next.s;
  const imageY = (anchor.y - next.ty) / next.s;
  return {
    tx: anchor.x - imageX * s,
    ty: anchor.y - imageY * s,
    s,
  };
}

function _computeNormalizedCenter(vp, imgDims, containerDims) {
  if (!imgDims?.w || !containerDims?.w) return { nx: 0.5, ny: 0.5 };
  const cx = (containerDims.w / 2 - vp.tx) / (vp.s * imgDims.w);
  const cy = (containerDims.h / 2 - vp.ty) / (vp.s * imgDims.h);
  return {
    nx: Math.min(1, Math.max(0, cx)),
    ny: Math.min(1, Math.max(0, cy)),
  };
}

// ─── The reducer ─────────────────────────────────────────────────────

export function cameraReducer(state, action) {
  const { type } = action || {};
  if (!COMMAND_TYPES.has(type)) {
    record({ type, unauthorized: true, reason: "unknown_command" });
    if (IS_DEV) {
      // Loud in dev, silent in prod.
      // eslint-disable-next-line no-console
      console.warn("[cameraController] rejected unknown command:", type);
    }
    return state;
  }
  const prev = state.vp || DEFAULT_VP;
  let next = prev;
  const {
    imgDims, containerDims, anchor, dxPx, dyPx, newScale, factor,
    persistedViewport, nx, ny, prevContainer, targetScale, maxZoom = 8,
  } = action;

  // Universal pre-computed-next form (used by the SheetCanvas adapter).
  // Every command may accept an authoritative `next` viewport that the
  // caller has already computed (e.g. the SheetCanvas.zoomAroundPoint
  // closure performs the identical anchor-preserving math). When
  // present, the reducer trusts it and records provenance. This keeps
  // the historical `setVp((prev) => nextVp)` call pattern legitimately
  // authorized while preserving the named-command vocabulary.
  const hasAuthoritativeNext = action.next
    && Number.isFinite(action.next.tx)
    && Number.isFinite(action.next.ty)
    && Number.isFinite(action.next.s);
  if (hasAuthoritativeNext && type !== "ROTATE_VIEW" && type !== "CANCEL_INERTIA") {
    next = { tx: action.next.tx, ty: action.next.ty, s: action.next.s };
    const changed0 = next.tx !== prev.tx || next.ty !== prev.ty || next.s !== prev.s;
    record({
      type,
      source: action.source || "unknown",
      user_initiated: !!action.user_initiated,
      unauthorized: false,
      changed: changed0,
      prev: { ...prev }, next: { ...next },
    });
    if (!changed0) return state;
    return { ...state, vp: next, lastCommand: type, lastCommandAt: Date.now() };
  }

  switch (type) {
    case "INITIAL_FIT": {
      if (!imgDims || !containerDims) break;
      next = fitAndCenter(imgDims, containerDims);
      break;
    }
    case "RESTORE_VIEWPORT": {
      if (!persistedViewport) break;
      const fit = imgDims && containerDims
        ? computeFitScale(imgDims, containerDims)
        : null;
      if (!isPlausibleViewport(persistedViewport, fit)) {
        // Reject stale persisted state — fall back to initial fit if we
        // can, otherwise no-op.
        if (imgDims && containerDims) {
          next = fitAndCenter(imgDims, containerDims);
          record({ type, note: "rejected_stale_persisted" });
        }
        break;
      }
      next = { ...persistedViewport };
      break;
    }
    case "USER_PAN": {
      next = { ...prev, tx: prev.tx + (dxPx || 0), ty: prev.ty + (dyPx || 0) };
      break;
    }
    case "USER_WHEEL_ZOOM":
    case "USER_TRACKPAD_ZOOM":
    case "USER_PINCH_ZOOM": {
      // Every anchored zoom MUST supply an anchor point.
      if (!anchor) {
        record({ type, unauthorized: true, reason: "missing_anchor" });
        break;
      }
      const raw = newScale != null
        ? newScale
        : prev.s * (factor || 1);
      next = zoomAroundPoint({ vp: prev, point: anchor, newScale: raw });
      next = _clamp(next, { imgDims, containerDims, anchor, maxZoom });
      break;
    }
    case "USER_BUTTON_ZOOM": {
      const raw = newScale != null
        ? newScale
        : prev.s * (factor || 1);
      const center = anchor || (containerDims
        ? { x: containerDims.w / 2, y: containerDims.h / 2 }
        : { x: 0, y: 0 });
      next = zoomAroundPoint({ vp: prev, point: center, newScale: raw });
      next = _clamp(next, { imgDims, containerDims, anchor: center, maxZoom });
      break;
    }
    case "FOCUS_OBJECT": {
      if (nx == null || ny == null || !imgDims || !containerDims) break;
      const s = targetScale != null ? targetScale : prev.s;
      next = centerOnNormalizedPoint({
        nx, ny, scale: s, imgDims, containerDims,
      });
      break;
    }
    case "PRESERVE_CENTER_AFTER_RESIZE": {
      // A container resize MUST NOT change scale. It MUST preserve the
      // normalized (nx, ny) centre that the operator was viewing.
      if (!imgDims || !containerDims || !prevContainer) break;
      const { nx: cx, ny: cy } = _computeNormalizedCenter(
        prev, imgDims, prevContainer,
      );
      next = centerOnNormalizedPoint({
        nx: cx, ny: cy, scale: prev.s, imgDims, containerDims,
      });
      break;
    }
    case "RESET_VIEW": {
      if (!imgDims || !containerDims) break;
      next = fitAndCenter(imgDims, containerDims);
      break;
    }
    case "ROTATE_VIEW":
      // Reserved. Rotation not yet enabled; recorded but no state change.
      break;
    case "CANCEL_INERTIA":
      // Signal-only command — inertia is owned by the gesture controller.
      break;
    case "APPLY_ANIMATION_FRAME": {
      // Per-frame commit from inertia or snap-back animation. Caller
      // supplies the fully-computed `next` viewport; the reducer's job
      // is to record the mutation in the ledger so provenance is
      // preserved. This does NOT clamp or apply anchoring — the animation
      // driver is responsible for bounds. It is USER_INITIATED = false.
      if (action.next && Number.isFinite(action.next.tx)
          && Number.isFinite(action.next.ty) && Number.isFinite(action.next.s)) {
        next = { tx: action.next.tx, ty: action.next.ty, s: action.next.s };
      }
      break;
    }
    default:
      break;
  }

  const changed = next !== prev &&
    (next.tx !== prev.tx || next.ty !== prev.ty || next.s !== prev.s);
  record({
    type,
    source: action.source || "unknown",
    user_initiated: !!action.user_initiated,
    unauthorized: false,
    changed,
    prev: { ...prev }, next: { ...next },
  });

  if (!changed) return state;
  return { ...state, vp: next, lastCommand: type, lastCommandAt: Date.now() };
}

export const CAMERA_COMMAND_TYPES = Array.from(COMMAND_TYPES);

// ─── Initial state factory ───────────────────────────────────────────

export function initialCameraState() {
  return { vp: { ...DEFAULT_VP }, lastCommand: null, lastCommandAt: null };
}
